/**
 * AuthProvider - Authentication Context for v2 Dashboard
 *
 * SECURITY: Uses HttpOnly cookies for token storage (set by server).
 * No tokens are accessible to JavaScript - server validates on each request.
 *
 * Provides authentication state and methods via React Context.
 * Handles:
 * - Login/logout
 * - Token refresh (scheduled based on expiry, not polling)
 * - User state
 * - Protected route handling
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AuthUser,
  getUser,
  getToken,
  signOut as authSignOut,
  refreshToken,
  fetchCurrentUser,
  getTokenExpirySeconds,
  isAuthInvalidated,
  onAuthStateChanged,
  ServerUnreachableError
} from '../services/auth';
import { logger } from '../../utils/logger';
import { trackEvent, resetUser } from '../../config/posthog.config';
import { HiExclamationTriangle } from 'react-icons/hi2';
import { VBButton } from '../components/ui/VBButton';

// Auth context types
interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isLoggingOut: boolean;
  isAuthenticated: boolean;
  /** True when the server cannot be reached (502, CORS, network down) */
  connectionError: boolean;
  login: () => void;
  logout: () => Promise<void>;
  clearCacheAndLogout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  /** Re-attempt server connection after a connection error */
  retryConnection: () => void;
}

// Export for test utilities
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Refresh buffer - refresh 5 minutes before expiry
const REFRESH_BUFFER_SECONDS = 300;

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [connectionError, setConnectionError] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const refreshRetryCount = useRef(0);
  const MAX_REFRESH_RETRIES = 3;

  // Schedule token refresh based on actual expiry time
  const scheduleRefresh = useCallback(() => {
    // Clear any existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }

    const secondsRemaining = getTokenExpirySeconds();

    // If token is already expired or no expiry info, attempt immediate refresh
    // This handles the case where the scheduled refresh was delayed (e.g., tab was hidden)
    if (secondsRemaining <= 0) {
      logger.debug('[AuthProvider] Token expired or no expiry, attempting immediate refresh');
      refreshRetryCount.current = 0;
      refreshToken()
        .then((success) => {
          if (success) {
            refreshRetryCount.current = 0;
            scheduleRefresh();
          } else {
            // If auth is definitively dead (401), don't bother retrying
            if (isAuthInvalidated()) {
              logger.warn('[AuthProvider] Auth invalidated, not retrying refresh');
              refreshRetryCount.current = 0;
              return;
            }
            refreshRetryCount.current++;
            if (refreshRetryCount.current < MAX_REFRESH_RETRIES) {
              logger.warn(
                `[AuthProvider] Refresh failed, retry ${refreshRetryCount.current}/${MAX_REFRESH_RETRIES} in 30s`
              );
              refreshTimeoutRef.current = setTimeout(() => {
                scheduleRefresh();
              }, 30000);
            } else {
              logger.warn('[AuthProvider] Refresh failed after max retries, session may expire');
              refreshRetryCount.current = 0;
            }
          }
        })
        .catch((err) => {
          logger.error('[AuthProvider] Refresh threw unexpectedly:', err);
          refreshRetryCount.current++;
          if (refreshRetryCount.current < MAX_REFRESH_RETRIES && !isAuthInvalidated()) {
            logger.warn(
              `[AuthProvider] Scheduling retry after unexpected error, ${refreshRetryCount.current}/${MAX_REFRESH_RETRIES}`
            );
            refreshTimeoutRef.current = setTimeout(() => {
              scheduleRefresh();
            }, 30000);
          } else {
            refreshRetryCount.current = 0;
          }
        });
      return;
    }

    // Schedule refresh REFRESH_BUFFER_SECONDS before expiry
    const refreshInSeconds = Math.max(secondsRemaining - REFRESH_BUFFER_SECONDS, 10);
    logger.debug(`[AuthProvider] Scheduling refresh in ${refreshInSeconds}s`);

    refreshTimeoutRef.current = setTimeout(async () => {
      logger.info('[AuthProvider] Executing scheduled token refresh');
      const success = await refreshToken();
      if (success) {
        // Reset retry count on success
        refreshRetryCount.current = 0;
        // Schedule next refresh
        scheduleRefresh();
      } else {
        refreshRetryCount.current++;
        if (refreshRetryCount.current < MAX_REFRESH_RETRIES) {
          // Retry after 30s - don't kick the user out
          // They can continue using cached data until an API call fails
          logger.warn(
            `[AuthProvider] Refresh failed, retry ${refreshRetryCount.current}/${MAX_REFRESH_RETRIES} in 30s`
          );
          refreshTimeoutRef.current = setTimeout(() => {
            scheduleRefresh();
          }, 30000);
        } else {
          // Max retries reached - stop retrying but don't force logout
          // User will be prompted to re-auth when they try something that needs auth
          logger.warn('[AuthProvider] Refresh failed after max retries, session may expire');
          refreshRetryCount.current = 0;
        }
      }
    }, refreshInSeconds * 1000);
  }, []);

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      setConnectionError(false);

      try {
        // Check if we appear to have a session
        const hasSession = !!getToken();

        if (hasSession) {
          // Fetch current user from server (validates cookie)
          const currentUser = await fetchCurrentUser();
          if (currentUser) {
            setUser(currentUser);
            // Schedule refresh based on token expiry
            scheduleRefresh();
          } else {
            // Server rejected auth (401 / invalid session) - clear local state
            setUser(null);
          }
        } else {
          // Check for stored user (session storage)
          const storedUser = getUser();
          if (storedUser) {
            // Try to validate with server
            const currentUser = await fetchCurrentUser();
            setUser(currentUser);
            if (currentUser) {
              scheduleRefresh();
            }
          }
        }
      } catch (err) {
        if (err instanceof ServerUnreachableError) {
          // Server is down / CORS blocked (e.g., 502 without CORS headers).
          // Show "server unavailable" UI instead of redirecting to login,
          // which would cause an infinite redirect loop (LoginPage sees stale
          // sessionStorage and redirects back to the protected route).
          logger.error('[AuthProvider] Server unreachable during init:', err.cause);
          setConnectionError(true);
        } else {
          logger.error('[AuthProvider] Init error:', err);
        }
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();

    // Subscribe to auth state changes (cross-tab)
    const unsubscribe = onAuthStateChanged((newUser) => {
      setUser(newUser);
      if (newUser) {
        scheduleRefresh();
      }
    });

    // Cleanup on unmount
    return () => {
      unsubscribe();
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [scheduleRefresh]);

  // Pause refresh when tab is hidden, resume when visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab hidden - clear refresh timeout
        if (refreshTimeoutRef.current) {
          clearTimeout(refreshTimeoutRef.current);
          refreshTimeoutRef.current = null;
        }
      } else {
        // Tab visible - reschedule if we have a user
        if (user) {
          scheduleRefresh();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, scheduleRefresh]);

  // Login handler - redirect to login page
  const login = useCallback(() => {
    // Store current path for redirect after login (validate at write time)
    const returnTo = location.pathname + location.search;
    if (returnTo !== '/v2/login' && returnTo.startsWith('/') && !returnTo.startsWith('//')) {
      sessionStorage.setItem('auth_return_to', returnTo);
    }
    navigate('/v2/login');
  }, [navigate, location]);

  // Logout handler
  const logout = useCallback(async () => {
    setIsLoggingOut(true);
    setIsLoading(true);

    try {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }

      trackEvent('user_signed_out');
      resetUser();

      logger.info('[AuthProvider] Starting logout');
      await authSignOut();
      setUser(null);

      logger.info('[AuthProvider] Logout complete, navigating to login');
      setIsLoading(false);
      setIsLoggingOut(false);
      navigate('/v2/login', { replace: true });
    } catch (err) {
      logger.error('[AuthProvider] Logout error:', err);
      setIsLoading(false);
      setIsLoggingOut(false);
      navigate('/v2/login', { replace: true });
    }
  }, [navigate]);

  // Logout with cache clear (clears localStorage too)
  const clearCacheAndLogout = useCallback(async () => {
    setIsLoggingOut(true);
    setIsLoading(true);

    try {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }

      trackEvent('user_cleared_cache');
      resetUser();

      localStorage.clear();
      logger.info('[AuthProvider] localStorage cleared');

      logger.info('[AuthProvider] Starting clear cache and logout');
      await authSignOut();
      setUser(null);

      setIsLoading(false);
      setIsLoggingOut(false);
      navigate('/v2/login', { replace: true });
    } catch (err) {
      logger.error('[AuthProvider] Clear cache error:', err);
      localStorage.clear();
      setIsLoading(false);
      setIsLoggingOut(false);
      navigate('/v2/login', { replace: true });
    }
  }, [navigate]);

  // Refresh user data
  const refreshUser = useCallback(async () => {
    try {
      const currentUser = await fetchCurrentUser();
      setUser(currentUser);
    } catch (err) {
      logger.error('[AuthProvider] Failed to refresh user:', err);
    }
  }, []);

  // Retry server connection after a connection error
  const retryConnection = useCallback(() => {
    if (!connectionError) return;
    logger.info('[AuthProvider] Retrying server connection...');
    // Reset state and re-run init by forcing a remount-like flow
    setConnectionError(false);
    setIsLoading(true);
    setUser(null);

    (async () => {
      try {
        const currentUser = await fetchCurrentUser();
        if (currentUser) {
          setUser(currentUser);
          scheduleRefresh();
        }
      } catch (err) {
        if (err instanceof ServerUnreachableError) {
          logger.error('[AuthProvider] Server still unreachable');
          setConnectionError(true);
        } else {
          logger.error('[AuthProvider] Retry error:', err);
        }
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [connectionError, scheduleRefresh]);

  // Context value
  const value = useMemo<AuthContextType>(
    () => ({
      user,
      isLoading,
      isLoggingOut,
      isAuthenticated: !!user,
      connectionError,
      login,
      logout,
      clearCacheAndLogout,
      refreshUser,
      retryConnection
    }),
    [
      user,
      isLoading,
      isLoggingOut,
      connectionError,
      login,
      logout,
      clearCacheAndLogout,
      refreshUser,
      retryConnection
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Loading skeleton for auth screens
 */
function AuthLoadingSkeleton() {
  return (
    <div className="flex items-center justify-center h-screen bg-vb-background dark:bg-vb-background-dark">
      <div className="animate-pulse flex flex-col items-center gap-4">
        <div className="h-12 w-12 bg-vb-surface-hover dark:bg-vb-surface-hover-dark rounded-full" />
        <div className="h-4 w-32 bg-vb-surface-hover dark:bg-vb-surface-hover-dark rounded" />
      </div>
    </div>
  );
}

/**
 * Higher-order component for protected routes
 */
export function withAuth<P extends object>(WrappedComponent: React.ComponentType<P>): React.FC<P> {
  const WithAuth: React.FC<P> = (props) => {
    const { isAuthenticated, isLoading, connectionError, retryConnection, login } = useAuth();

    useEffect(() => {
      if (!isLoading && !isAuthenticated && !connectionError) {
        login();
      }
    }, [isLoading, isAuthenticated, connectionError, login]);

    if (connectionError) {
      return <ServerUnavailableScreen onRetry={retryConnection} />;
    }

    if (isLoading) {
      return <AuthLoadingSkeleton />;
    }

    if (!isAuthenticated) {
      return null; // Will redirect to login
    }

    return <WrappedComponent {...props} />;
  };

  WithAuth.displayName = `withAuth(${WrappedComponent.displayName || WrappedComponent.name})`;
  return WithAuth;
}

/**
 * Server unavailable screen — shown when the API server cannot be reached.
 * Prevents the redirect loop between ProtectedRoute and LoginPage that occurs
 * when the server is down (502 without CORS headers).
 * Auto-retries every 15 seconds with a manual retry button.
 */
function ServerUnavailableScreen({ onRetry }: { onRetry: () => void }) {
  const [countdown, setCountdown] = useState(15);

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          onRetry();
          return 15;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [onRetry]);

  return (
    <div className="flex items-center justify-center h-screen bg-vb-background dark:bg-vb-background-dark">
      <div className="text-center max-w-sm px-6">
        <div className="w-16 h-16 bg-vb-danger/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <HiExclamationTriangle className="w-8 h-8 text-vb-danger" />
        </div>
        <h2 className="text-lg font-semibold text-vb-text-primary dark:text-vb-text-primary-dark mb-2">
          Unable to connect to server
        </h2>
        <p className="text-sm text-vb-text-secondary dark:text-vb-text-secondary-dark mb-6">
          The server is not responding. This is usually temporary — it may be restarting or
          undergoing maintenance.
        </p>
        <VBButton
          variant="primary"
          onClick={() => {
            setCountdown(15);
            onRetry();
          }}
        >
          Retry now
        </VBButton>
        <p className="mt-3 text-xs text-vb-text-tertiary dark:text-vb-text-tertiary-dark">
          Auto-retrying in {countdown}s
        </p>
      </div>
    </div>
  );
}

/**
 * Component for protecting routes
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, isLoggingOut, connectionError, retryConnection, login } =
    useAuth();

  useEffect(() => {
    // Don't redirect to login if we're in the middle of logging out
    // or if the server is unreachable (would cause infinite redirect loop)
    if (!isLoading && !isAuthenticated && !isLoggingOut && !connectionError) {
      login();
    }
  }, [isLoading, isAuthenticated, isLoggingOut, connectionError, login]);

  // Server unreachable — show error screen with retry instead of redirect loop
  if (connectionError) {
    return <ServerUnavailableScreen onRetry={retryConnection} />;
  }

  // Show loading during auth check or logout
  if (isLoading || isLoggingOut) {
    return <AuthLoadingSkeleton />;
  }

  if (!isAuthenticated) {
    return null; // Will redirect to login
  }

  return <>{children}</>;
}

export default AuthProvider;
